// src/services/api/adminReservations.api.ts
import { apiRequest } from "./client";

export type AdminReservationUser = {
    id: string;
    email?: string | null;
    first_name?: string | null;
    last_name?: string | null;
};

export type AdminReservationSeat = {
    id: string;
    tour_city_id: string;
    hall_scheme_element_id: string;
    price_id?: string | null;
    status: string;
    barcode?: string | null;
    count: number;
    row?: number | string | null;
    place?: number | string | null;
    sector_id?: string | null;
    sector_name?: string | null;
    price?: number | null;
};

export type AdminReservationItem = {
    id: string;
    name: string;
    user_id?: string | null;
    tour_city_id?: string | null;
    status: "active" | "deleted" | string;
    created_by?: string | null;
    created_at: string;
    deleted_at?: string | null;
    user?: AdminReservationUser | null;
    creator?: AdminReservationUser | null;
    seats_count: number;
};

export type AdminReservationDetail = AdminReservationItem & {
    hall_seat_prices?: Array<{
        id: string;
        tour_city_id: string;
        hall_scheme_element_id: string;
        price_id?: string | null;
        status: string;
        count: number;
    }>;
};

type ListResponse = {
    items: AdminReservationItem[];
    total: number;
    page: number;
    page_size: number;
};

// GET /admin-reservations/{id}
export const getAdminReservationRequest = async (
    reservationId: string,
): Promise<AdminReservationDetail> => {
    return apiRequest<AdminReservationDetail>(`/admin-reservations/${reservationId}`, {
        method: "GET",
        auth: true,
    });
};

// GET /admin-reservations?tour_city_id=...&status=active
export const listAdminReservationsRequest = async (params: {
    tour_city_id?: string;
    status?: "active" | "deleted";
    page?: number;
    page_size?: number;
}): Promise<ListResponse> => {
    const query = new URLSearchParams();
    if (params.tour_city_id) query.set("tour_city_id", params.tour_city_id);
    if (params.status) query.set("status", params.status);
    if (params.page) query.set("page", String(params.page));
    if (params.page_size) query.set("page_size", String(params.page_size));

    return apiRequest<ListResponse>(`/admin-reservations?${query.toString()}`, {
        method: "GET",
        auth: true,
    });
};

// POST /admin-reservations
export const createAdminReservationRequest = async (payload: {
    name: string;
    user_id?: string | null;
    tour_city_id?: string | null;
}): Promise<AdminReservationDetail> => {
    return apiRequest<AdminReservationDetail>("/admin-reservations", {
        method: "POST",
        auth: true,
        body: JSON.stringify(payload),
        headers: { "Content-Type": "application/json" },
    });
};

// POST /admin-reservations/{id}/seats
export const addSeatsToAdminReservationRequest = async (
    reservationId: string,
    hall_seat_price_ids: string[],
): Promise<{
    added: string[];
    skipped: Array<{ id: string; reason: string }>;
    reservation: AdminReservationDetail;
}> => {
    return apiRequest(`/admin-reservations/${reservationId}/seats`, {
        method: "POST",
        auth: true,
        body: JSON.stringify({ hall_seat_price_ids }),
        headers: { "Content-Type": "application/json" },
    });
};

// GET /admin-reservations/{id}/seats
export const getAdminReservationSeatsRequest = async (
    reservationId: string,
): Promise<AdminReservationSeat[]> => {
    return apiRequest<AdminReservationSeat[]>(
        `/admin-reservations/${reservationId}/seats`,
        { method: "GET", auth: true },
    );
};

// DELETE /admin-reservations/{id}/seats/{seat_price_id}
export const removeSeatFromAdminReservationRequest = async (
    reservationId: string,
    seatPriceId: string,
): Promise<AdminReservationDetail> => {
    return apiRequest<AdminReservationDetail>(
        `/admin-reservations/${reservationId}/seats/${seatPriceId}`,
        { method: "DELETE", auth: true },
    );
};

// DELETE /admin-reservations/{id}  — отменяет всю бронь, все места обратно в active
export const cancelAdminReservationRequest = async (
    reservationId: string,
): Promise<AdminReservationDetail> => {
    return apiRequest<AdminReservationDetail>(
        `/admin-reservations/${reservationId}`,
        { method: "DELETE", auth: true },
    );
};


// GET /admin-reservations/{id}/lieferschein-pdf
// Скачивает PDF Lieferschein для админ-брони.
export const downloadAdminReservationLieferscheinRequest = async (
    reservationId: string,
): Promise<{ blob: Blob; filename: string }> => {
    // используем fetch напрямую, так как apiRequest не возвращает blob
    const { API_BASE_URL } = await import("../config/env");
    const { getAccessToken } = await import("../storage/token.storage");

    const token = getAccessToken();
    const response = await fetch(
        `${API_BASE_URL}/admin-reservations/${reservationId}/lieferschein-pdf`,
        {
            method: "GET",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
    );

    if (!response.ok) {
        let detail = `HTTP ${response.status}`;
        try {
            const data = await response.json();
            if (data?.detail) detail = String(data.detail);
        } catch {
            // ignore
        }
        throw new Error(detail);
    }

    const blob = await response.blob();

    // вытаскиваем имя файла из Content-Disposition, если есть
    let filename = `lieferschein-${reservationId}.pdf`;
    const cd = response.headers.get("content-disposition");
    if (cd) {
        const m = cd.match(/filename\*?=(?:UTF-8'')?\"?([^\";]+)\"?/i);
        if (m && m[1]) filename = decodeURIComponent(m[1]);
    }

    return { blob, filename };
};
